<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ConversationApi from 'dashboard/api/conversations';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversationId: { type: [String, Number], required: true },
  contactName: { type: String, default: '' },
});

const emit = defineEmits(['cancel', 'updated', 'update:show']);
const { t } = useI18n();
const isSending = ref(false);
const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const close = () => {
  if (isSending.value) return;
  localShow.value = false;
  emit('cancel');
};

const send = async () => {
  if (isSending.value) return;
  isSending.value = true;
  try {
    const { data } = await ConversationApi.sendVtigerLead(props.conversationId);
    useAlert(t('CONVERSATION.VTIGER_RECORD.SEND_SUCCESS'));
    localShow.value = false;
    emit('updated', data);
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CONVERSATION.VTIGER_RECORD.SEND_ERROR')
    );
  } finally {
    isSending.value = false;
  }
};
</script>

<template>
  <woot-modal
    v-model:show="localShow"
    :on-close="close"
    :close-on-backdrop-click="!isSending"
    class="!items-start [&>div]:!top-12 [&>div]:sticky"
  >
    <form class="flex w-full flex-col gap-5 px-6 py-6" @submit.prevent="send">
      <div>
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ t('CONVERSATION.VTIGER_RECORD.SEND_TITLE') }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('CONVERSATION.VTIGER_RECORD.SEND_DESCRIPTION') }}
        </p>
      </div>

      <div class="rounded-lg border border-n-weak bg-n-alpha-1 p-4">
        <p v-if="contactName" class="text-sm font-medium text-n-slate-12">
          {{ contactName }}
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('CONVERSATION.VTIGER_RECORD.SEND_MODULE') }}
        </p>
        <p class="mt-3 text-xs text-n-slate-11">
          {{ t('CONVERSATION.VTIGER_RECORD.SEND_WARNING') }}
        </p>
      </div>

      <div class="flex justify-end gap-2">
        <NextButton
          type="button"
          color="slate"
          faded
          :label="t('CONVERSATION.VTIGER_RECORD.CANCEL')"
          :disabled="isSending"
          @click="close"
        />
        <NextButton
          type="submit"
          color="blue"
          :label="t('CONVERSATION.VTIGER_RECORD.SEND_CONFIRM')"
          :is-loading="isSending"
        />
      </div>
    </form>
  </woot-modal>
</template>
